import * as i0 from '@angular/core';
import { Injectable, Directive, Input, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { map, switchMap, tap, takeWhile, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';

var AwareSecuritySymbols;
(function (AwareSecuritySymbols) {
    AwareSecuritySymbols["AnyPermission"] = "*";
})(AwareSecuritySymbols || (AwareSecuritySymbols = {}));

class AwareSecurityPermissionProvider {
}

class AwareSecurityService {
    constructor(permissionProvider) {
        this.permissionProvider = permissionProvider;
    }
    isGod(unitId) {
        if (!unitId) {
            return this.permissionProvider.getPermissions().pipe(map(({ roles }) => {
                for (const i in roles) {
                    if (this._isGod(roles[i])) {
                        return true;
                    }
                }
                return false;
            }));
        }
        return this._getMyPermissions(unitId).pipe(map((myPermissions) => (myPermissions ? this._isGod(myPermissions) : false)));
    }
    /**
     * Checks if all the passed in permissions are granted
     * @param permission Array of permissions
     * @param unitId Optional unit ID param
     * @returns boolean
     */
    isGranted(permission, unitId) {
        return this.isGod().pipe(switchMap((isGod) => {
            if (isGod) {
                return of(true);
            }
            return this._getMyPermissions(unitId).pipe(map((myPermissions) => (myPermissions ? this._hasPermission(permission, myPermissions) : false)));
        }));
    }
    /**
     * Checks if ANY of the passed in permissions are granted
     * @param permission Array of permissions
     * @param unitId Optional unit ID param
     * @returns boolean
     */
    isGrantedAny(permission, unitId) {
        return this.isGod().pipe(switchMap((isGod) => {
            if (isGod) {
                return of(true);
            }
            return this._getMyPermissions(unitId).pipe(map((myPermissions) => {
                return myPermissions ? this._hasPermission(permission, myPermissions, true) : false;
            }));
        }));
    }
    _getMyPermissions(unitId) {
        let units = null;
        return this.permissionProvider.getUnitId().pipe(tap((u) => {
            units = unitId ? [unitId] : u;
        }), switchMap(() => this.permissionProvider.getPermissions()), map(({ permissions, roles }) => {
            let roleIds;
            for (const unit of units) {
                if (permissions[unit]) {
                    roleIds = permissions[unit];
                    break;
                }
            }
            /**
             * If I have no roles on this unit, deny access
             */
            if (!roleIds) {
                return false;
            }
            /**
             * Join permissions from all my roles on active unit
             */
            const joinedPermissionArray = roleIds.reduce((acc, curr) => {
                return (acc = [...acc, ...roles[curr]]);
            }, []);
            return joinedPermissionArray;
        }));
    }
    /**
     * Method to match required permissions vs another list of permissions.
     * As of now, every permission must be matched. To turn on "or" matching,
     * replace every "continue loop1;" with a "return true" instead, and replace
     * the final return statement with a "return false".
     * @param permission String or array of strings of permissions to check against.
     * @param myPermission My list of permissions
     * @returns {boolean} True if I have access
     */
    _hasPermission(permission, myPermission, matchAny = false) {
        if (!permission) {
            return true;
        }
        const grantedPermissions = new Set();
        permission = Array.isArray(permission) ? permission : [permission];
        if (this._isGod(myPermission)) {
            return true;
        }
        loop1: for (const requires of permission) {
            const requiredSplit = requires.split('.');
            loop2: for (const my of myPermission) {
                const mySplit = my.split('.');
                if (requiredSplit[0] !== mySplit[0]) {
                    continue loop2;
                }
                loop3: for (let index in requiredSplit) {
                    const i = parseInt(index, 10);
                    const part = requiredSplit[i];
                    if (mySplit[i] === AwareSecuritySymbols.AnyPermission) {
                        if (matchAny) {
                            return true;
                        }
                        grantedPermissions.add(requires);
                        continue loop1;
                    }
                    if (part !== mySplit[i]) {
                        continue loop2;
                    }
                    if (requiredSplit[i + 1]) {
                        /**
                         * If my permission doesnt include an additional part, assume I have access to
                         * everything beneath active part and return true
                         */
                        if (!mySplit[i + 1]) {
                            grantedPermissions.add(requires);
                            if (matchAny) {
                                return true;
                            }
                            continue loop1;
                        }
                        // Otherwise loop again
                        continue loop3;
                    }
                    grantedPermissions.add(requires);
                }
            }
        }
        const grants = permission.map((p) => grantedPermissions.has(p));
        if (matchAny) {
            return grants.some((g) => g);
        }
        return !grants.some((g) => !g);
    }
    _isGod(myPermissions) {
        return myPermissions.find((permission) => permission === '*.*.*') ? true : false;
    }
}
AwareSecurityService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityService, deps: [{ token: AwareSecurityPermissionProvider }], target: i0.ɵɵFactoryTarget.Injectable });
AwareSecurityService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: AwareSecurityPermissionProvider }]; } });

class AwareSecurityIsGodDirective {
    constructor(template, viewContainer, awareSecurityService) {
        this.template = template;
        this.viewContainer = viewContainer;
        this.awareSecurityService = awareSecurityService;
    }
    ngOnInit() {
        this.ALIVE = true;
        this.awareSecurityService
            .isGod()
            .pipe(takeWhile(() => this.ALIVE))
            .subscribe((isGranted) => {
            if (isGranted && !this.IS_IN_VIEW) {
                this.viewContainer.createEmbeddedView(this.template);
                this.IS_IN_VIEW = true;
            }
            else if (!isGranted && this.IS_IN_VIEW) {
                this.viewContainer.clear();
                this.IS_IN_VIEW = false;
            }
        });
    }
    ngOnDestroy() {
        this.ALIVE = false;
    }
}
AwareSecurityIsGodDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityIsGodDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: AwareSecurityService }], target: i0.ɵɵFactoryTarget.Directive });
AwareSecurityIsGodDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.0", type: AwareSecurityIsGodDirective, selector: "[awIsGod]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityIsGodDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[awIsGod]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: AwareSecurityService }]; } });

class AwareSecurityIsGrantedDirective {
    constructor(template, viewContainer, awareSecurityService) {
        this.template = template;
        this.viewContainer = viewContainer;
        this.awareSecurityService = awareSecurityService;
        this.destroy$ = new Subject();
    }
    set awIsGranted(permission) {
        this.awareSecurityService
            .isGranted(permission)
            .pipe(takeUntil(this.destroy$))
            .subscribe((isGranted) => {
            if (isGranted && !this.IS_IN_VIEW) {
                this.viewContainer.createEmbeddedView(this.template);
                this.IS_IN_VIEW = true;
            }
            else if (!isGranted && this.IS_IN_VIEW) {
                this.viewContainer.clear();
                this.IS_IN_VIEW = false;
            }
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
AwareSecurityIsGrantedDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityIsGrantedDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: AwareSecurityService }], target: i0.ɵɵFactoryTarget.Directive });
AwareSecurityIsGrantedDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.0", type: AwareSecurityIsGrantedDirective, selector: "[awIsGranted]", inputs: { awIsGranted: "awIsGranted" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityIsGrantedDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[awIsGranted]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: AwareSecurityService }]; }, propDecorators: { awIsGranted: [{
                type: Input
            }] } });

class AwareSecurityIsGrantedAnyDirective {
    constructor(template, viewContainer, awareSecurityService) {
        this.template = template;
        this.viewContainer = viewContainer;
        this.awareSecurityService = awareSecurityService;
        this.destroy$ = new Subject();
    }
    set awIsGrantedAny(permission) {
        this.awareSecurityService
            .isGrantedAny(permission)
            .pipe(takeUntil(this.destroy$))
            .subscribe((isGranted) => {
            if (isGranted && !this.IS_IN_VIEW) {
                this.viewContainer.createEmbeddedView(this.template);
                this.IS_IN_VIEW = true;
            }
            else if (!isGranted && this.IS_IN_VIEW) {
                this.viewContainer.clear();
                this.IS_IN_VIEW = false;
            }
        });
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
AwareSecurityIsGrantedAnyDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityIsGrantedAnyDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }, { token: AwareSecurityService }], target: i0.ɵɵFactoryTarget.Directive });
AwareSecurityIsGrantedAnyDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.1.0", type: AwareSecurityIsGrantedAnyDirective, selector: "[awIsGrantedAny]", inputs: { awIsGrantedAny: "awIsGrantedAny" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityIsGrantedAnyDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[awIsGrantedAny]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }, { type: AwareSecurityService }]; }, propDecorators: { awIsGrantedAny: [{
                type: Input
            }] } });

class AwareSecurityModule {
    static forRoot() {
        return {
            ngModule: AwareSecurityModule,
            providers: [AwareSecurityService],
        };
    }
}
AwareSecurityModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AwareSecurityModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityModule, declarations: [AwareSecurityIsGrantedDirective, AwareSecurityIsGodDirective, AwareSecurityIsGrantedAnyDirective], imports: [CommonModule], exports: [AwareSecurityIsGrantedDirective, AwareSecurityIsGodDirective, AwareSecurityIsGrantedAnyDirective] });
AwareSecurityModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityModule, providers: [AwareSecurityService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.0", ngImport: i0, type: AwareSecurityModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [AwareSecurityIsGrantedDirective, AwareSecurityIsGodDirective, AwareSecurityIsGrantedAnyDirective],
                    exports: [AwareSecurityIsGrantedDirective, AwareSecurityIsGodDirective, AwareSecurityIsGrantedAnyDirective],
                    providers: [AwareSecurityService],
                }]
        }] });

/*
 * Public API Surface of aware-security
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AwareSecurityIsGodDirective, AwareSecurityIsGrantedAnyDirective, AwareSecurityIsGrantedDirective, AwareSecurityModule, AwareSecurityPermissionProvider, AwareSecurityService, AwareSecuritySymbols };
